import type { TFunction } from "i18next";

import type { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import type { OutboxCampaignOption } from "../../../List/schema/outbox-counts";
import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

/**
 * The campaign selection once the Outbox's lists become `nextLists` (PREC-4291), decided in the
 * same step as the list change so no request pairs the new lists with a campaign they lost:
 * - a campaign whose own list (from `options`, named or not) is still selected stays;
 * - one whose list was removed is dropped; with no list selected (all lists) every placed one stays;
 * - one the options cannot place (deleted, from an old bookmark) is dropped: it is on no list.
 * `options` must describe the lists selected *before* the change. While they are unknown the
 * selection is left as it is. Returns the same array when nothing is dropped.
 */
export const campaignIdsForLists = (
	ids: string[],
	options: Pick<OutboxCampaignOption, "id" | "listId">[] | undefined,
	nextLists: Pick<CustomListsAutocompleteForSequencerItem, "id">[]
): string[] => {
	if (!options) {
		return ids;
	}

	const nextListIds = nextLists.map((list) => list.id);
	const kept = ids.filter((id) => {
		const option = options.find((candidate) => candidate.id === id);

		return !!option && (nextListIds.length === 0 || nextListIds.includes(option.listId));
	});

	return kept.length === ids.length ? ids : kept;
};

/**
 * The campaign filter's items: each option by its name, a campaign without a name as
 * "<its list> campaign", and each selected campaign the options cannot place as "Campaign from
 * link", so a link's selection is always visible and clearable without claiming a list for it.
 * Nothing before the options have loaded, so no label flashes up and away.
 */
export const campaignFilterItems = (
	options: OutboxCampaignOption[] | undefined,
	selectedIds: string[],
	t: TFunction
): DropdownWithCheckboxesItem[] => {
	if (!options) {
		return [];
	}

	const placed = options.map((option) => ({
		id: option.id,
		title: option.name || t("outbox.filters.unnamedCampaignOfList", { list: option.listName }),
	}));
	const unplaced = selectedIds
		.filter((id) => !options.some((option) => option.id === id))
		.map((id) => ({ id, title: t("outbox.filters.campaignFromLink") }));

	return [...placed, ...unplaced];
};
